import { generateGuestToken, getLockerDetailsNearToUser } from '../api/api';
import { GUESS_TOKEN, LOCAL_STORAGE, USER_LOCATION_DETAILS } from '../assets/constants/BrowserStorageKeys';
import { storeSecureItemInSpecifiedStorage } from './BrowserStorageAccessMiddleware';
import i18n from 'i18next';

const DEFAULT_LOCKER_DISTANCE = 3000;
const updateLoadingMessage = (onLoadingMessage, message) => {
  if (typeof onLoadingMessage === 'function') {
    onLoadingMessage(message);
  }
};

const createNearbyLockerError = (code, message, cause = null) => {
  const error = new Error(message);
  error.code = code;
  error.cause = cause;
  return error;
};

export const getCurrentBrowserLocation = (
  geolocation = typeof window !== 'undefined' ? window.navigator?.geolocation : null,
  options = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  }
) =>
  new Promise((resolve, reject) => {
    if (!geolocation) {
      reject(
        createNearbyLockerError(
          i18n.t('error_geolocation_not_supported_this_browser')
        )
      );
      return;
    }

    geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        let errorCode = 'LOCATION_FETCH_FAILED';

        if (error?.code === 1) {
          errorCode = 'LOCATION_PERMISSION_DENIED';
        } else if (error?.code === 2) {
          errorCode = 'LOCATION_UNAVAILABLE';
        } else if (error?.code === 3) {
          errorCode = 'LOCATION_TIMEOUT';
        }

        reject(
          createNearbyLockerError(
            errorCode,
            error?.message || i18n.t('error_unable_fetch_current_location'),
            error
          )
        );
      },
      options
    );
  });

export const fetchNearbyLockersForLocation = async ({
  userLocation,
  onLoadingMessage,
  distance = DEFAULT_LOCKER_DISTANCE,
  shouldStoreLocation = true,
} = {}) => {
  if (!userLocation?.lat || !userLocation?.lng) {
    throw createNearbyLockerError(
      i18n.t('error_message_valid_latitude_longitude_required')
    );
  }

  updateLoadingMessage(onLoadingMessage, i18n.t('pleaseWaitText'));

  const responseGenerateToken = await generateGuestToken();
  const guestToken = responseGenerateToken?.data?.token;

  if (guestToken) {
    storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, GUESS_TOKEN, guestToken);
  }

  if (shouldStoreLocation) {
    storeSecureItemInSpecifiedStorage(
      LOCAL_STORAGE,
      USER_LOCATION_DETAILS,
      userLocation
    );
  }

  if (responseGenerateToken?.status !== 'success') {
    throw createNearbyLockerError(
      i18n.t('error_message_user_not_able_to_authenticate_gest_user')
    );
  }

  updateLoadingMessage(onLoadingMessage, i18n.t('info_message_getting_lockers_near_you'));

  const response = await getLockerDetailsNearToUser({
    distance,
    location: userLocation,
    token: guestToken,
  });

  if (response?.data?.status !== 'success') {
    throw createNearbyLockerError(
      i18n.t('error_message_failed_fetch_lockers')
    );
  }

  updateLoadingMessage(onLoadingMessage, i18n.t('info_message_opening_locker_map'));

  return {
    lockersData: response?.data?.data || [],
    userLocation,
    token: guestToken,
  };
};

export const getNearbyLockerErrorMessage = (error) => {
      return i18n.t('error_message_something_went_wrong_while_finding_lockers');
};
